// W41 — Regisztráció (céges fiók létrehozása).
//
// FONTOS TERMÉK-DÖNTÉS, amit a képernyő ki is mond a felhasználónak:
// ez a regisztráció ÚJ CÉGET hoz létre, és a regisztrálót teszi meg annak
// adminisztrátorává. Meglévő céghez csatlakozni továbbra sem lehet innen —
// azt a cég adminja végzi az „Új felhasználó” funkcióval. Enélkül bárki
// beírhatná egy idegen cég nevét, és bekerülne annak az adataihoz.
//
// Az adatmodell a Flutter oldallal közös (lásd docs/01-funkcionalis-specifikacio.md):
//   projects/{companyId}  - a CÉG dokumentuma (name, desc, maxUser)
//   users/{uid}           - a felhasználó, projects[] = cégtagságok
//
// A két írás egyetlen `writeBatch`-ben megy: ha a cég létrejönne, de a
// felhasználó nem, a fiók cég nélkül maradna, és a belépés a „nincs céged”
// hibaoldalra futna — vagyis egy használhatatlan, de létező fiók keletkezne.

import {
  createUserWithEmailAndPassword,
  deleteUser,
  updateProfile,
  type Auth,
} from 'firebase/auth';
import { doc, writeBatch, type Firestore } from 'firebase/firestore';
import { t } from '../lib/i18n';
import { h, icon, icons } from '../lib/dom';
import { metaEsemeny } from '../lib/meta-pixel';
import { gaEsemeny } from '../lib/google-analytics';

/// Az új cég alapértelmezett létszámkerete.
///
/// A modell kötelezőnek jelöli (`maxUser`), a Flutter admin felülete pedig
/// ez alapján tiltja a további felvételt. Egy önkiszolgáló regisztrációnál
/// nincs honnan tudnunk a valódi keretet, ezért indulásnak adunk egy értéket,
/// amit a cég adminja később átír.
const DEFAULT_MAX_USER = 10;

/// A Firebase regisztrációs hibakódjainak szótárbeli megfelelője.
function errorMessage(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return t('register.errEmailInUse');
    case 'auth/invalid-email':
      return t('auth.invalidEmail');
    case 'auth/weak-password':
      return t('register.errWeakPassword');
    case 'auth/operation-not-allowed':
      return t('register.errDisabled');
    case 'auth/network-request-failed':
      return t('auth.networkError');
    case 'auth/too-many-requests':
      return t('register.errTooManyRequests');
    // A Firestore utasitotta vissza az irast, nem az Auth. Ez nem a
    // felhasznalo hibaja es nem is mulo uzem zavar: a biztonsagi szabalyok
    // nem engedik az onkiszolgalo cegletrehozast. Kulon uzenetet kap, mert a
    // „probald ujra" itt sosem segitene.
    case 'permission-denied':
      return t('register.errNotAllowed');
    default:
      return t('register.errUnknown');
  }
}

/// Egy mező a hozzá tartozó címkével és hibaüzenet-hellyel.
///
/// Azért külön típus, mert a hibát a mező ALATT kell megjeleníteni (nem
/// egyetlen közös dobozban), hogy látszódjon, melyik mezőről van szó.
type Field = {
  wrap: HTMLElement;
  input: HTMLInputElement;
  setError: (message: string | null) => void;
};

function field(options: {
  id: string;
  label: string;
  type: string;
  placeholder: string;
  autocomplete: string;
  hint?: string;
}): Field {
  const input = h('input', {
    class: 'input',
    type: options.type,
    id: options.id,
    autocomplete: options.autocomplete,
    placeholder: options.placeholder,
  });

  const error = h('span', {
    class: 'body-sm w6',
    style: { color: 'var(--err)', display: 'none' },
  });

  const hint = options.hint
    ? h('span', { class: 'body-sm tm', text: options.hint })
    : null;

  const wrap = h(
    'div',
    { class: 'field' },
    h('label', { class: 'flbl', for: options.id, text: options.label }),
    input,
    ...(hint ? [hint] : []),
    error,
  );

  function setError(message: string | null): void {
    if (message === null) {
      error.style.display = 'none';
      input.removeAttribute('aria-invalid');
      input.classList.remove('err');
      if (hint) hint.style.display = '';
      return;
    }
    error.textContent = message;
    error.style.display = 'block';
    input.setAttribute('aria-invalid', 'true');
    input.classList.add('err');
    // A súgó és a hiba egyszerre zsúfolt lenne; a hiba a fontosabb.
    if (hint) hint.style.display = 'none';
  }

  input.addEventListener('input', () => setError(null));

  return { wrap, input, setError };
}

export function registerPage(options: {
  auth: Auth;
  db: Firestore;
  onLogin: () => void;
}): HTMLElement {
  const { auth, db, onLogin } = options;

  const company = field({
    id: 'reg-company',
    label: t('register.companyName'),
    type: 'text',
    placeholder: t('register.companyPlaceholder'),
    autocomplete: 'organization',
  });
  const lastName = field({
    id: 'reg-lastname',
    label: t('register.lastName'),
    type: 'text',
    placeholder: t('register.lastNamePlaceholder'),
    autocomplete: 'family-name',
  });
  const firstName = field({
    id: 'reg-firstname',
    label: t('register.firstName'),
    type: 'text',
    placeholder: t('register.firstNamePlaceholder'),
    autocomplete: 'given-name',
  });
  const email = field({
    id: 'reg-email',
    label: t('auth.email'),
    type: 'email',
    placeholder: t('auth.emailPlaceholder'),
    autocomplete: 'username',
  });
  const password = field({
    id: 'reg-password',
    label: t('auth.password'),
    type: 'password',
    placeholder: t('auth.passwordPlaceholder'),
    autocomplete: 'new-password',
    hint: t('register.passwordHint'),
  });
  const password2 = field({
    id: 'reg-password2',
    label: t('register.passwordRepeat'),
    type: 'password',
    placeholder: t('auth.passwordPlaceholder'),
    autocomplete: 'new-password',
  });

  const errorBox = h('div', {
    class: 'row top g12',
    role: 'alert',
    style: {
      display: 'none',
      padding: '12px 14px',
      borderRadius: '10px',
      background: 'var(--err-soft)',
      border: '1px solid var(--s-rej-bd)',
    },
  });

  function showError(message: string): void {
    errorBox.replaceChildren(
      h('span', { class: 'ic', style: { color: 'var(--err)' } }, icon(icons.warning)),
      h('span', { class: 'body-sm w6', style: { color: 'var(--err)' }, text: message }),
    );
    errorBox.style.display = 'flex';
    errorBox.setAttribute('tabindex', '-1');
    errorBox.focus();
  }

  function hideError(): void {
    errorBox.style.display = 'none';
  }

  const submit = h('button', {
    class: 'btn btn-primary btn-lg',
    type: 'submit',
    style: { width: '100%' },
    text: t('register.submit'),
  });

  let busy = false;
  function setBusy(value: boolean): void {
    busy = value;
    submit.disabled = value;
    submit.textContent = value ? t('register.submitBusy') : t('register.submit');
  }

  /// Mezőnkénti ellenőrzés. Igazat ad, ha minden rendben van.
  ///
  /// Mindegyik mezőt megvizsgáljuk, nem állunk meg az elsőnél — így a
  /// felhasználó egyszerre látja, mi hiányzik, nem kattintásonként egyet.
  function validate(): boolean {
    let ok = true;

    if (company.input.value.trim().length < 2) {
      company.setError(t('register.needCompany'));
      ok = false;
    }
    if (lastName.input.value.trim().length === 0) {
      lastName.setError(t('register.needLastName'));
      ok = false;
    }
    if (firstName.input.value.trim().length === 0) {
      firstName.setError(t('register.needFirstName'));
      ok = false;
    }
    // Szándékosan enyhe minta: a valódi ellenőrzés a Firebase-é, mi csak az
    // elgépelést fogjuk meg, mielőtt hálózatra mennénk.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.input.value.trim())) {
      email.setError(t('auth.provideEmail'));
      ok = false;
    }
    if (password.input.value.length < 8) {
      password.setError(t('register.needPassword8'));
      ok = false;
    }
    if (password2.input.value !== password.input.value) {
      password2.setError(t('register.passwordMismatch'));
      ok = false;
    }

    return ok;
  }

  const form = h(
    'form',
    {
      class: 'col g16',
      novalidate: 'true',
      onSubmit: async (event: Event) => {
        event.preventDefault();
        if (busy) return;
        hideError();

        if (!validate()) {
          // Az első hibás mezőre ugrunk, hogy ne kelljen keresgélni.
          const first = [company, lastName, firstName, email, password, password2]
            .find((f) => f.input.getAttribute('aria-invalid') === 'true');
          first?.input.focus();
          return;
        }

        setBusy(true);
        try {
          const credential = await createUserWithEmailAndPassword(
            auth,
            email.input.value.trim(),
            password.input.value,
          );
          const uid = credential.user.uid;

          const companyRef = doc(db, 'projects', uid);
          const userRef = doc(db, 'users', uid);

          // A cég azonosítója szándékosan a regisztráló uid-ja: így a
          // biztonsági szabályokban egyetlen összehasonlítással eldönthető,
          // hogy a cég létrehozója írja-e — és nem kell külön ütközésmentes
          // azonosítót kitalálni.
          const batch = writeBatch(db);
          batch.set(companyRef, {
            name: company.input.value.trim(),
            desc: null,
            maxUser: DEFAULT_MAX_USER,
          });
          batch.set(userRef, {
            id: uid,
            email: email.input.value.trim(),
            firstName: firstName.input.value.trim(),
            lastName: lastName.input.value.trim(),
            createdAt: new Date().toISOString(),
            projects: [{ id: uid, role: 'ADMIN' }],
          });
          await batch.commit();

          // Konverzio a Meta fele: a fiok es a ceg letrejott. Hozzajarulas
          // nelkul nem csinal semmit; szemelyes adatot nem kuldunk.
          metaEsemeny('CompleteRegistration');
          // Ugyanez a GA4 fele (csak Statisztika-hozzajarulassal). A regisztracio
          // ma kizarolag e-mail + jelszoval tortenik.
          gaEsemeny('sign_up', { method: 'password' });

          // A megjelenített név a Firebase Auth oldalán is jól jöjjön ki,
          // mert a session betöltése ezt használja tartaléknak.
          //
          // SAJAT try/catch: ez a batch UTAN fut, es csak kozmetika. Ha a
          // kozos agra engednenk kifutni, a lenti visszavonas torolne a
          // fiokot — pedig a ceg es a felhasznalo dokumentuma ekkor MAR
          // letrejott. Az eredmeny ket arva dokumentum es semmi fiok lenne,
          // vagyis rosszabb, mint a hianyzo megjelenitett nev.
          try {
            await updateProfile(credential.user, {
              displayName: `${lastName.input.value.trim()} ${firstName.input.value.trim()}`,
            });
          } catch (error) {
            console.warn('[BlazeData] A megjelenitett nev beallitasa nem sikerult.', error);
          }

          // A továbblépésről az auth-figyelő gondoskodik (main.ts): a
          // regisztráció egyben be is léptet.
        } catch (error) {
          const code = (error as { code?: string }).code ?? '';

          // A FIOK MAR LETEZHET. A `createUserWithEmailAndPassword` sikerult,
          // es csak az utana kovetkezo Firestore-iras hasalt el — ilyenkor a
          // felhasznalo egy dokumentum nelkuli, bejelentkezett fiokkal marad,
          // a cime pedig „mar foglalt" lesz, tehat ujra sem tud probalkozni.
          //
          // Ezert visszavonjuk: a frissen letrehozott fiokot a kliens MEG
          // torolni tudja sajat magat (a muvelet friss bejelentkezest igenyel,
          // ez pedig az). Ha ez sem megy, legalabb kileptetunk, hogy ne
          // maradjon egy fel-fiok munkamenete nyitva.
          const created = auth.currentUser;
          if (created !== null && !code.startsWith('auth/')) {
            try {
              await deleteUser(created);
            } catch {
              await auth.signOut().catch(() => undefined);
            }
          }

          showError(errorMessage(code));
          setBusy(false);
          return;
        }
        setBusy(false);
      },
    },
    company.wrap,
    h(
      'div',
      // A ds.css-ben nincs sortoro sor-osztaly, a ket nevmezonek viszont
      // keskeny kepernyon egymas ala kell kerulnie, kulonben 44 px ala szorul.
      { class: 'row g12', style: { flexWrap: 'wrap' } },
      h('div', { style: { flex: '1 1 160px' } }, lastName.wrap),
      h('div', { style: { flex: '1 1 160px' } }, firstName.wrap),
    ),
    email.wrap,
    password.wrap,
    password2.wrap,
    submit,
  );

  const notice = h(
    'div',
    {
      class: 'row top g12',
      style: {
        padding: '12px 14px',
        borderRadius: '10px',
        background: 'var(--info-soft)',
        border: '1px solid var(--border)',
      },
    },
    h('span', { class: 'ic', style: { color: 'var(--info)' } }, icon(icons.shield)),
    h('span', {
      class: 'body-sm ts',
      text: t('register.notice'),
    }),
  );

  const brandPanel = h(
    'aside',
    {
      class: 'brand-panel',
      style: {
        background: 'var(--accent)',
        color: 'var(--on-accent)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px',
      },
    },
    h(
      'span',
      { class: 'logo', style: { fontSize: '18px', color: 'var(--on-accent)' } },
      h('span', { class: 'mk', style: { background: 'rgba(255,255,255,.18)' } }, icon(icons.spark)),
      'Blaze',
    ),
    h(
      'div',
      { class: 'col', style: { gap: '18px' } },
      h('h2', {
        class: 'display',
        style: {
          fontSize: '44px',
          color: 'var(--on-accent)',
          whiteSpace: 'pre-line',
        },
        text: t('register.brandHeadline'),
      }),
      h('p', {
        class: 'body-lg',
        style: { maxWidth: '400px', opacity: '.92' },
        text: t('register.brandLead'),
      }),
    ),
    h(
      'div',
      { class: 'row g12', style: { opacity: '.9' } },
      h('span', { class: 'ic' }, icon(icons.shield)),
      h('span', { class: 'body-sm', text: t('register.brandPrivacy') }),
    ),
  );

  return h(
    'div',
    { class: 'login-layout' },
    brandPanel,
    h(
      'main',
      {
        style: {
          flexGrow: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 16px',
        },
      },
      h(
        'div',
        { class: 'col', style: { width: '420px', maxWidth: '100%', gap: '22px' } },
        h(
          'div',
          { class: 'col', style: { gap: '6px' } },
          h('h1', { class: 'h1', text: t('register.title') }),
          h('p', { class: 'body ts', text: t('register.subtitle') }),
        ),
        notice,
        errorBox,
        form,
        h(
          'div',
          { class: 'row g6', style: { justifyContent: 'center' } },
          h('span', { class: 'body-sm ts', text: t('register.haveAccount') }),
          h('button', {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            text: t('auth.loginTitle'),
            onClick: onLogin,
          }),
        ),
      ),
    ),
  );
}
